import * as React from "react";
import { Plus, Search } from "lucide-react";
import { Button, Input, InputGroup, Popover } from "@/components/ui";
import { Review } from "@/services/bedrock/dtos/Review";
import { QuoteEdit } from "@/services/bedrock/dtos/DesignRenderGroup";
import { ReviewPickerRow, ReviewRowSkeleton, ReviewSummaryLine } from "./ReviewPickerRow";

export interface ReviewPickerProps {
  reviews: Review[];
  selectedIds: number[];
  pinnedId: number | null;
  min: number;
  max: number;
  loading: boolean;
  onChange: (ids: number[]) => void;
  onAddBest: () => void;
  /** This post's quote edits, by review id. */
  quoteEdits?: Record<number, QuoteEdit>;
  onEditQuote?: (review: Review) => void;
}

export const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

export function reviewRangeLine(min: number, max: number): string {
  return min === max ? `Pick ${plural(min, "review")}.` : `Pick ${min} to ${max} reviews.`;
}

export const ReviewPicker: React.FC<ReviewPickerProps> = ({
  reviews,
  selectedIds,
  pinnedId,
  min,
  max,
  loading,
  onChange,
  onAddBest,
  quoteEdits,
  onEditQuote,
}) => {
  const [search, setSearch] = React.useState("");
  const [addOpen, setAddOpen] = React.useState(false);
  const byId = React.useMemo(() => new Map(reviews.map(review => [Number(review.id), review])), [reviews]);
  const selected = selectedIds.map(id => byId.get(id)).filter((review): review is Review => !!review);
  const remaining = reviews.filter(review => !selectedIds.includes(Number(review.id)));
  const othersExist = reviews.some(review => Number(review.id) !== pinnedId);
  const full = selectedIds.length >= max;

  const move = (id: number, dir: -1 | 1) => {
    const from = selectedIds.indexOf(id);
    const to = from + dir;
    if (to < 0 || to >= selectedIds.length || selectedIds[to] === pinnedId) return;
    const next = [...selectedIds];
    [next[from], next[to]] = [next[to], next[from]];
    onChange(next);
  };

  const query = search.trim().toLowerCase();
  const matches = query
    ? remaining.filter(review =>
        [review.displayName, review.condensedQuote, review.content].some(text => text?.toLowerCase().includes(query))
      )
    : remaining;

  return (
    <div className="flex flex-col gap-3" data-testid="review-picker">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-base font-semibold text-slate-800">Your reviews in this post</h3>
          <p className="text-sm text-slate-500">{reviewRangeLine(min, max)}</p>
        </div>
        {!loading && remaining.length > 0 && !full && (
          <Button variant="outline" size="sm" onClick={onAddBest}>
            Add my top reviews
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col gap-2">
          <ReviewRowSkeleton />
          <ReviewRowSkeleton />
          <ReviewRowSkeleton />
        </div>
      ) : (
        <>
          {selected.length > 0 && (
            <ol className="flex flex-col gap-2">
              {selected.map((review, index) => {
                const id = Number(review.id);
                const pinned = id === pinnedId;
                return (
                  <ReviewPickerRow
                    key={id}
                    review={review}
                    index={index}
                    pinned={pinned}
                    canMoveUp={index > 0 && selectedIds[index - 1] !== pinnedId}
                    canMoveDown={index < selected.length - 1}
                    onMove={dir => move(id, dir)}
                    onRemove={() => onChange(selectedIds.filter(other => other !== id))}
                    quoteEdit={quoteEdits?.[id]}
                    onEditQuote={onEditQuote ? () => onEditQuote(review) : undefined}
                  />
                );
              })}
            </ol>
          )}

          {!othersExist && (
            <p className="text-sm text-slate-500">
              You don't have other reviews ready to share yet. This design works great with just 1 review.
            </p>
          )}

          {remaining.length > 0 && !full && (
            <div className="flex flex-wrap items-center gap-2">
              <Popover open={addOpen} onOpenChange={setAddOpen}>
                <Popover.Trigger
                  render={
                    <Button variant="ghost" size="sm">
                      <Plus />
                      Add another review
                    </Button>
                  }
                />
                <Popover.Content className="w-[min(90vw,420px)] p-2">
                  <InputGroup
                    className="mb-2"
                    control={controlProps => (
                      <Input
                        {...controlProps}
                        value={search}
                        placeholder="Search reviews…"
                        aria-label="Search reviews"
                        onChange={event => setSearch(event.target.value)}
                      />
                    )}
                    prepend={<Search />}
                  />
                  <ul className="flex max-h-72 flex-col gap-1 overflow-y-auto">
                    {matches.map(review => (
                      <li key={review.id}>
                        <button
                          type="button"
                          className="w-full rounded-lg p-2 text-left hover:bg-slate-50 cursor-pointer"
                          onClick={() => {
                            onChange([...selectedIds, Number(review.id)]);
                            setAddOpen(false);
                          }}
                        >
                          <ReviewSummaryLine review={review} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </Popover.Content>
              </Popover>
              <span className="text-xs text-slate-500">{remaining.length} more ready to share</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};
